import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { LogoIcon } from '@/components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SharedTaskPage - A quiet moment of shared focus
 * 
 * Someone is here, working on something.
 * The task is the object in the room.
 */
const SharedTaskPage = () => {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharedTask, setSharedTask] = useState(null);

  useEffect(() => {
    const fetchSharedTask = async () => {
      try {
        const response = await fetch(`${API_URL}/api/tasks/shared/${shareId}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('not_found');
          } else if (response.status === 410) {
            setError('expired');
          } else {
            setError('unknown');
          }
          return;
        }
        
        const data = await response.json();
        setSharedTask(data);
      } catch (err) {
        setError('unknown');
      } finally {
        setLoading(false);
      }
    };

    fetchSharedTask();
  }, [shareId]);

  // Loading state - minimal
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-[#A1A1AA] animate-spin" />
      </div>
    );
  }

  // Error state - soft and simple
  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center px-6">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="text-center max-w-sm"
        >
          <p className="text-[#71717A] text-lg mb-8">
            {error === 'expired' 
              ? "This moment has passed."
              : "Nothing here right now."}
          </p>
          <Link 
            to="/app"
            className="text-[#0CBCBF] hover:text-[#0AA8B3] transition-colors text-sm font-medium"
          >
            Find your thread
          </Link>
        </motion.div>
      </div>
    );
  }

  const firstName = sharedTask.user_name?.split(' ')[0] || sharedTask.user_name;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Minimal header - just the logo */}
      <header className="fixed top-0 left-0 right-0 z-50">
        <div className="max-w-2xl mx-auto px-6 py-5">
          <Link to="/" className="inline-flex items-center gap-2 opacity-40 hover:opacity-60 transition-opacity">
            <LogoIcon className="w-5 h-5" />
          </Link>
        </div>
      </header>

      {/* Main content - the task is the room */}
      <main className="min-h-screen flex items-center justify-center px-6">
        <motion.div 
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          className="w-full max-w-md text-center"
        >
          {/* Authenticated view - shows the task */}
          {sharedTask.is_authenticated && sharedTask.task_name ? (
            <>
              {/* User context - whisper quiet */}
              <p className="text-[#A1A1AA] text-sm mb-6">
                {firstName} is here, working on this
              </p>

              {/* The task - the object in the room */}
              <motion.h1 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.4 }}
                className="text-2xl md:text-3xl font-normal text-[#27272A] leading-relaxed tracking-tight"
              >
                {sharedTask.task_name}
              </motion.h1>

              {/* Own task preview note */}
              {sharedTask.is_own_task && (
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-[#C4C4C4] text-xs mt-12"
                >
                  This is what others see
                </motion.p>
              )}
            </>
          ) : (
            /* Non-authenticated view - gentle invitation */
            <>
              {/* User presence */}
              <p className="text-[#A1A1AA] text-sm mb-4">
                {firstName} is here
              </p>

              {/* Soft message with app name in blue */}
              <h1 className="text-xl text-[#52525B] font-normal mb-8">
                working in <span className="text-[#0CBCBF]">Get Where Was I?</span>
              </h1>

              {/* Gentle CTA - orange */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Link 
                  to="/app"
                  className="inline-block px-6 py-3 bg-[#FFF4EC] hover:bg-[#FFE8D9] text-[#F58227] rounded-full text-sm font-medium transition-colors"
                >
                  See the app in action
                </Link>
              </motion.div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default SharedTaskPage;
